import { Button, Card, Checkbox, Flex, Form, Input, InputNumber, Space, Typography } from 'antd';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import { Measure } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { useMutation } from '../../../jsonapi-provider';

const SUCCESS_ADD_MESSAGE = 'Measure added successfully';
const SUCCESS_UPDATE_MESSAGE = 'Measure updated successfully';

interface MeasureFormProps {
  measure: Measure;
  onSuccess: (measure: Measure) => void;
  onCancel: () => void;
}

const MeasureForm = (props: MeasureFormProps): ReactNode => {
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const { measure, onSuccess, onCancel } = props;
  const isEditMode = useMemo(() => !!measure.id, [measure.id]);
  const [isReportingUnitEnabled, setReportingUnitEnabled] = useState<boolean>(
    !!measure.reportingUnit
  );
  const initialValues = useMemo(
    () => ({
      ...measure,
    }),
    [measure]
  );
  const [mutateMeasure, measures] = useMutation<Measure>(
    measure?.id ? ['measure', measure.id] : ['measure']
  );

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const onSubmit = (submitted: Measure & Record<string, unknown>): void => {
    const measureToSubmit = new Measure(
      submitted.id,
      submitted.name,
      isReportingUnitEnabled && submitted.reportingUnit ? submitted.reportingUnit : undefined,
      isReportingUnitEnabled && submitted.reportingUnitMultiplier
        ? submitted.reportingUnitMultiplier
        : undefined
    );
    from(mutateMeasure(measureToSubmit))
      .pipe(
        switchMap((res) =>
          res.error ? throwError(() => new Error(res.error?.title)) : of(res.data!)
        ),
        tap(() => {
          notification.success({
            message: ' ',
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
          });
        }),
        tap(onSuccess),
        catchError((error: Error) => {
          notification.error({
            message: '',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="measure-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? measure.name : 'New Serving Measure'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Flex vertical gap="middle" style={{ marginBlockEnd: 'var(--ant-margin-lg)' }}>
            <div>
              <Checkbox
                defaultChecked={isReportingUnitEnabled}
                onChange={(event) => {
                  setReportingUnitEnabled(event.target.checked);
                }}
                style={{ marginInlineEnd: 'var(--ant-margin-xxs)' }}
              />{' '}
              Enable reporting unit
            </div>

            {isReportingUnitEnabled && (
              <Space.Compact>
                <Form.Item
                  label="Reporting Unit"
                  name="reportingUnit"
                  rules={[{ required: true, message: 'Please enter a unit name' }]}
                  style={{ flexGrow: 1, marginBlockEnd: 'var(--ant-margin-sm)' }}
                >
                  <Input addonBefore={1} addonAfter="=" maxLength={50} placeholder="Unit name" />
                </Form.Item>

                <Form.Item
                  label=" "
                  name="reportingUnitMultiplier"
                  rules={[{ required: true, message: 'Please enter a conversion multiplier' }]}
                  style={{ flexGrow: 1, marginBlockEnd: 'var(--ant-margin-sm)' }}
                  initialValue={1}
                >
                  <InputNumber addonAfter={measure.name} precision={2} placeholder="0.01" />
                </Form.Item>
              </Space.Compact>
            )}
          </Flex>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Button
                block
                htmlType="submit"
                type="primary"
                disabled={measures.isLoading}
                loading={measures.isLoading}
              >
                Save
              </Button>
              <Button block onClick={onCancel} disabled={measures.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default MeasureForm;
